// src/documents/exportProcessoControleQualidade.js

import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import grupoLogo from '../assets/grupo_logo.png';

const PRODUCTS_COLLECTION = 'products';

/**
 * Loads and adds the company logo to the workbook
 */
const loadLogo = async (workbook) => {
    try {
        const logoResponse = await fetch(grupoLogo);
        if (!logoResponse.ok) {
            throw new Error(`Falha ao carregar o logo (status ${logoResponse.status}).`);
        }
        const blob = await logoResponse.blob();

        const mime = blob.type || 'image/png';
        let ext = mime.split('/').pop().toLowerCase() || 'png';

        const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

        const supportedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'];
        if (!supportedExtensions.includes(ext)) {
            throw new Error(`Formato de imagem não suportado: "${ext}". Converta o logo para PNG ou JPEG.`);
        }

        if (ext === 'jpeg') ext = 'jpg';

        const logoImageId = workbook.addImage({ base64, extension: ext });

        if (logoImageId === undefined || logoImageId === null) {
            throw new Error(
                'Logo não pôde ser adicionado à planilha. ' +
                'O arquivo pode estar corrompido ou o formato real não corresponde ao MIME detectado.'
            );
        }

        return logoImageId;
    } catch (error) {
        console.error('Logo loading error:', error);
        throw new Error(`Não foi possível carregar o logo: ${error.message}. A geração foi cancelada.`);
    }
};

/**
 * Helper: apply medium border to all cells in a row for specified columns
 */
const applyRowBorder = (worksheet, row, colCount, style = 'medium') => {
    for (let c = 1; c <= colCount; c++) {
        const cell = worksheet.getCell(row, c);
        cell.border = {
            top: { style },
            left: { style: c === 1 ? style : 'thin' },
            bottom: { style },
            right: { style: c === colCount ? style : 'thin' }
        };
    }
};

/**
 * Helper: create a merged title row (dark background, white text)
 * Same styling as exportProcessoManipulacao and exportProcessoEnvase
 */
const addTitleRow = (worksheet, row, text, colCount = 3) => {
    worksheet.mergeCells(row, 1, row, colCount);
    const cell = worksheet.getCell(row, 1);
    cell.value = text;
    cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E4057' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(row).height = 25;
    applyRowBorder(worksheet, row, colCount, 'thin');
};

/**
 * Helper: create section header row (light gray-blue background)
 * Matches the color scheme used in other sheets
 */
const addSectionHeader = (worksheet, row, text, colCount = 3) => {
    worksheet.mergeCells(row, 1, row, colCount);
    const cell = worksheet.getCell(row, 1);
    cell.value = text;
    cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A3A5C' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(row).height = 25;
    applyRowBorder(worksheet, row, colCount, 'thin');
};

/**
 * Creates a single product quality control worksheet
 */
const createProductSheet = (workbook, logoImageId, params) => {
    const {
        item,
        productData,
        order,         // ← ADD THIS
        sheetIndex
    } = params;

    let sheetName = `${item.productCode}_CQ_${item.lote || 'SEMLOTE'}`;
    if (sheetName.length > 31) sheetName = sheetName.substring(0, 31);

    const worksheet = workbook.addWorksheet(sheetName);

    // Column widths
    worksheet.getColumn(1).width = 35;
    worksheet.getColumn(2).width = 55;
    worksheet.getColumn(3).width = 40;

    let currentRow = 1;

    // ========================================================================
    // ROW 1: Logo + Title + Document Code
    // ========================================================================
    const row1 = worksheet.getRow(currentRow);
    row1.height = 70;

    if (logoImageId != null) {
        worksheet.addImage(logoImageId, {
            tl: { col: 0.1, row: 0.050 },
            ext: { width: 230, height: 85 },
            editAs: 'oneCell'
        });
    }

    worksheet.getCell('B1').value = 'PROCESSO DO CONTROLE DE QUALIDADE\nANÁLISE E ACOMPANHAMENTO DE PRODUTO ACABADO';
    worksheet.getCell('B1').font = { bold: true, size: 14 };
    worksheet.getCell('B1').alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

    worksheet.getCell('C1').value = 'FQ 22\nRev. 02';          // ← ADD THIS
    worksheet.getCell('C1').font = { bold: true, size: 12 };     // ← ADD THIS
    worksheet.getCell('C1').alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }; // ← ADD THIS

    ['A', 'B', 'C'].forEach(col => {
        worksheet.getCell(`${col}1`).border = {
            top: { style: 'medium' },
            left: { style: col === 'A' ? 'medium' : 'thin' },
            bottom: { style: 'thin' },
            right: { style: col === 'C' ? 'medium' : 'thin' }
        };
    });

    currentRow = 2;

    // ========================================================================
    // ROW 2: DADOS DO PRODUTO (section header)
    // ========================================================================
    addTitleRow(worksheet, currentRow, 'DADOS DO PRODUTO');
    currentRow = 3;

    // ========================================================================
    // ROW 3: PRODUTO
    // ========================================================================
    const row3 = worksheet.getRow(3);
    row3.height = 25;
    worksheet.mergeCells('A3:C3');
    worksheet.getCell('A3').value = `PRODUTO: ${item.productCode} - ${item.productName}`;
    worksheet.getCell('A3').font = { bold: true, size: 11 };
    worksheet.getCell('A3').alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
    applyRowBorder(worksheet, currentRow, 3, 'thin');
    currentRow = 4;

    // ========================================================================
    // ROW 4: DATA DE FABRICAÇÃO e DATA DE VALIDADE
    // ========================================================================
    const row4 = worksheet.getRow(4);
    row4.height = 25;
    worksheet.mergeCells('A4:B4');
    worksheet.getCell('A4').value = 'DATA DE FABRICAÇÃO: ____/____/____';
    worksheet.getCell('A4').font = { size: 11 };
    worksheet.getCell('A4').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 3, 'thin');

    worksheet.mergeCells('C4');
    worksheet.getCell('C4').value = 'DATA DE VALIDADE: ____/____/____';
    worksheet.getCell('C4').font = { size: 11 };
    worksheet.getCell('C4').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 3, 'thin');
    currentRow = 5;

    // ========================================================================
    // ROW 5: LOTE e O.P
    // ========================================================================
    const row5 = worksheet.getRow(5);
    row5.height = 25;
    worksheet.mergeCells('A5:B5');
    worksheet.getCell('A5').value = `LOTE: ${item.lote || '_______________'}`;
    worksheet.getCell('A5').font = { bold: true, size: 11 };
    worksheet.getCell('A5').alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('C5');
    worksheet.getCell('C5').value = `O.P: ${order.opCode || ''}`;
    worksheet.getCell('C5').font = { bold: true, size: 11 };
    worksheet.getCell('C5').alignment = { horizontal: 'left', vertical: 'middle' };

    applyRowBorder(worksheet, currentRow, 3, 'thin');
    currentRow = 6;

    // ========================================================================
    // ROW 6: ANÁLISES ORGANOLÉPTICAS E FÍSICO-QUÍMICAS NA FABRICAÇÃO
    // ========================================================================
    addTitleRow(worksheet, currentRow, 'ANÁLISES ORGANOLÉPTICAS E FÍSICO-QUÍMICAS NA FABRICAÇÃO');
    currentRow = 7;

    // ========================================================================
    // ROW 7: Table headers for analyses
    // ========================================================================
    const row7 = worksheet.getRow(7);
    row7.height = 30;

    const headers = ['ANÁLISES', 'VALORES PADRÕES', 'VALORES ENCONTRADOS'];
    headers.forEach((header, i) => {
        const cell = worksheet.getCell(7, i + 1);
        cell.value = header;
        cell.font = { bold: true, color: { argb: 'FF2E4057' }, size: 10 };     // ← Changed to match title row color
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };  // ← Light gray-blue
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    });
    applyRowBorder(worksheet, currentRow, 3, 'thin');  // ← Changed from 'medium' to 'thin'
    currentRow = 8;

    // ========================================================================
    // ROWS 8-12: Analysis criteria
    // ========================================================================
    const analyses = [
        { name: 'Cor', field: 'cor' },
        { name: 'Odor', field: 'odor' },
        { name: 'pH', field: 'ph' },
        { name: 'Viscosidade', field: 'viscosidade' },
        { name: 'Centrífuga', field: 'centrifuga' }
    ];

    // Get quality criteria from product data if available
    const qualityCriteria = productData.qualityCriteria || [];

    analyses.forEach((analysis, index) => {
        const row = worksheet.getRow(currentRow);
        row.height = 20;

        const criteria = qualityCriteria.find(qc =>
            qc.analysis?.toLowerCase() === analysis.name.toLowerCase()
        );
        const padrao = criteria?.specification || '';

        worksheet.getCell(currentRow, 1).value = analysis.name;
        worksheet.getCell(currentRow, 1).font = { bold: true, size: 10 };
        worksheet.getCell(currentRow, 1).alignment = { horizontal: 'left', vertical: 'middle' };

        worksheet.getCell(currentRow, 2).value = padrao;
        worksheet.getCell(currentRow, 2).font = { size: 10 };
        worksheet.getCell(currentRow, 2).alignment = { horizontal: 'left', vertical: 'middle' };

        worksheet.getCell(currentRow, 3).value = '';
        worksheet.getCell(currentRow, 3).font = { size: 10 };
        worksheet.getCell(currentRow, 3).alignment = { horizontal: 'left', vertical: 'middle' };

        applyRowBorder(worksheet, currentRow, 3, 'thin');
        currentRow++;
    });

    // ========================================================================
    // ROW 13: VISTO DO CONTROLE DE QUALIDADE
    // ========================================================================
    const row13 = worksheet.getRow(13);
    row13.height = 30;
    worksheet.mergeCells('A13:C13');
    worksheet.getCell('A13').value = 'VISTO DO CONTROLE DE QUALIDADE: ';
    worksheet.getCell('A13').font = { bold: true, size: 11 };
    worksheet.getCell('A13').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, 13, 3, 'thin');
    currentRow = 14;

    // ========================================================================
    // ROW 14: DATA DA LIBERAÇÃO PARA ENVASE
    // ========================================================================
    const row14 = worksheet.getRow(14);
    row14.height = 25;
    worksheet.mergeCells('A14:C14');
    worksheet.getCell('A14').value = 'DATA DA LIBERAÇÃO PARA ENVASE: ____/____/____';
    worksheet.getCell('A14').font = { bold: true, size: 11 };
    worksheet.getCell('A14').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, 14, 3, 'thin');
    currentRow = 15;

    // ========================================================================
    // ROW 15: OBSERVAÇÕES
    // ========================================================================
    const row15 = worksheet.getRow(15);
    row15.height = 25;
    worksheet.mergeCells('A15:C15');
    worksheet.getCell('A15').value = 'OBSERVAÇÕES:';
    worksheet.getCell('A15').font = { bold: true, size: 11 };
    worksheet.getCell('A15').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, 15, 3, 'thin');
    currentRow = 16;

    // ========================================================================
    // ROW 16: Empty observation line
    // ========================================================================
    const row16 = worksheet.getRow(16);
    row16.height = 25;
    worksheet.mergeCells('A16:C16');
    worksheet.getCell('A16').value = '';
    applyRowBorder(worksheet, 16, 3, 'thin');
    currentRow = 17;

    // ========================================================================
    // ROW 17: ACOMPANHAMENTOS FÍSICO-QUÍMICOS E ORGANOLÉPTICOS NA QUARENTENA
    // ========================================================================
    addTitleRow(worksheet, currentRow, 'ACOMPANHAMENTOS FÍSICO-QUÍMICOS E ORGANOLÉPTICOS NA QUARENTENA');
    currentRow = 18;

    // ========================================================================
    // Function to create quarantine follow-up blocks
    // ========================================================================
    const createQuarantineBlock = (startRow, titleText, dateText) => {
        // Header row
        const headerRow = worksheet.getRow(startRow);
        headerRow.height = 25;

        worksheet.getCell(startRow, 1).value = 'ANÁLISES';
        worksheet.getCell(startRow, 1).font = { bold: true, color: { argb: 'FF2E4057' }, size: 10 };
        worksheet.getCell(startRow, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
        worksheet.getCell(startRow, 1).alignment = { horizontal: 'center', vertical: 'middle' };

        worksheet.getCell(startRow, 2).value = titleText;
        worksheet.getCell(startRow, 2).font = { bold: true, color: { argb: 'FF2E4057' }, size: 10 };
        worksheet.getCell(startRow, 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
        worksheet.getCell(startRow, 2).alignment = { horizontal: 'center', vertical: 'middle' };

        worksheet.getCell(startRow, 3).value = dateText;
        worksheet.getCell(startRow, 3).font = { bold: true, color: { argb: 'FF2E4057' }, size: 10 };
        worksheet.getCell(startRow, 3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
        worksheet.getCell(startRow, 3).alignment = { horizontal: 'center', vertical: 'middle' };

        applyRowBorder(worksheet, startRow, 3, 'medium');

        let row = startRow + 1;

        // Analysis rows (Cor, Odor, pH, Viscosidade, Centrífuga)
        const quarantineAnalyses = ['Cor', 'Odor', 'pH', 'Viscosidade', 'Centrífuga'];
        quarantineAnalyses.forEach(analysis => {
            worksheet.getRow(row).height = 20;
            worksheet.getCell(row, 1).value = analysis;
            worksheet.getCell(row, 1).font = { bold: true, size: 10 };
            worksheet.getCell(row, 1).alignment = { horizontal: 'left', vertical: 'middle' };

            worksheet.mergeCells(row, 2, row, 3);
            worksheet.getCell(row, 2).value = '';
            worksheet.getCell(row, 2).font = { size: 10 };
            worksheet.getCell(row, 2).alignment = { horizontal: 'left', vertical: 'middle' };

            applyRowBorder(worksheet, row, 3, 'thin');
            row++;
        });

        // Visto row
        worksheet.getRow(row).height = 30;
        worksheet.mergeCells(row, 1, row, 3);
        worksheet.getCell(row, 1).value = 'VISTO DO CONTROLE DE QUALIDADE: ';
        worksheet.getCell(row, 1).font = { bold: true, size: 11 };
        worksheet.getCell(row, 1).alignment = { horizontal: 'left', vertical: 'middle' };
        applyRowBorder(worksheet, row, 3, 'thin');
        row++;

        // Observações row
        worksheet.getRow(row).height = 25;
        worksheet.mergeCells(row, 1, row, 3);
        worksheet.getCell(row, 1).value = 'OBSERVAÇÕES:';
        worksheet.getCell(row, 1).font = { bold: true, size: 11 };
        worksheet.getCell(row, 1).alignment = { horizontal: 'left', vertical: 'middle' };
        applyRowBorder(worksheet, row, 3, 'thin');
        row++;

        // Empty observation line
        worksheet.getRow(row).height = 25;
        worksheet.mergeCells(row, 1, row, 3);
        worksheet.getCell(row, 1).value = '';
        applyRowBorder(worksheet, row, 3, 'thin');
        row++;

        return row;
    };

    // ========================================================================
    // 30 DAYS BLOCK
    // ========================================================================
    currentRow = createQuarantineBlock(
        currentRow,
        'DADOS EM 30 DIAS',
        'DATA: ____/____/____'
    );


    // ========================================================================
    // 60 DAYS BLOCK
    // ========================================================================
    currentRow = createQuarantineBlock(
        currentRow,
        'DADOS EM 60 DIAS',
        'DATA: ____/____/____'
    );


    // ========================================================================
    // 90 DAYS BLOCK
    // ========================================================================
    currentRow = createQuarantineBlock(
        currentRow,
        'DADOS EM 90 DIAS',
        'DATA: ____/____/____'
    );

    //empty row
    currentRow++;


    // ========================================================================
    // Approval row
    // ========================================================================
    const aprovRow = worksheet.getRow(currentRow);
    aprovRow.height = 45;

    worksheet.mergeCells(`A${currentRow}:A${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'Elaborado por: Kátia Fernandes\nData: 11/05/2026';
    worksheet.getCell(`A${currentRow}`).font = { size: 9 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    worksheet.mergeCells(`B${currentRow}:B${currentRow}`);
    worksheet.getCell(`B${currentRow}`).value = 'Revisado por: Magali Murro\nData: 11/05/2026';
    worksheet.getCell(`B${currentRow}`).font = { size: 9 };
    worksheet.getCell(`B${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    worksheet.mergeCells(`C${currentRow}:C${currentRow}`);
    worksheet.getCell(`C${currentRow}`).value = 'Aprovado por: Nisrine El Hazzat\nData: 11/05/2026';
    worksheet.getCell(`C${currentRow}`).font = { size: 9 };
    worksheet.getCell(`C${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    // Thin borders for approval row
    for (let c = 1; c <= 3; c++) {
        worksheet.getCell(currentRow, c).border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
    }

    // Print setup
    worksheet.pageSetup.orientation = 'portrait';
    worksheet.pageSetup.fitToPage = true;
    worksheet.pageSetup.fitToWidth = 1;
    worksheet.pageSetup.fitToHeight = 1;
    worksheet.pageSetup.paperSize = 9;
};

/**
 * Exports the quality control process document for a production order
 */
export const exportProcessoControleQualidade = async ({
    order,
    allRawMaterials,
    allCategories,
    allProductTypes,
    showNotification
}) => {
    try {
        if (!order || !order.items || order.items.length === 0) {
            showNotification("Ordem sem produtos para gerar o processo de controle de qualidade.", 'warning');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const logoImageId = await loadLogo(workbook);

        for (let itemIndex = 0; itemIndex < order.items.length; itemIndex++) {
            const item = order.items[itemIndex];

            const productSnap = await getDoc(doc(db, PRODUCTS_COLLECTION, item.productId));
            if (!productSnap.exists()) continue;

            const productData = productSnap.data();

            createProductSheet(workbook, logoImageId, {
                item,
                productData,
                order,              // ← ADD THIS
                allRawMaterials,
                allCategories,
                allProductTypes,
                sheetIndex: itemIndex
            });
        }

        if (workbook.worksheets.length === 0) {
            showNotification("Não foi possível gerar o processo de controle de qualidade.", 'error');
            return;
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const fileName = `Processo_Controle_Qualidade_${order.opCode || 'OP'}_${new Date().getTime()}.xlsx`;
        saveAs(blob, fileName);

        showNotification(`Processo de controle de qualidade gerado com sucesso para ${workbook.worksheets.length} produto(s)!`, 'success');

    } catch (error) {
        console.error("Erro ao gerar processo de controle de qualidade:", error);
        showNotification(`Erro ao gerar processo: ${error.message}`, 'error');
    }
};